import React, { useState, useEffect } from 'react';
import { Suffixes, VerificationInputPayload, VerificationInputProps } from './VerificationInput.types';
import styles from './VerificationInput.module.scss';
import Select from '../../atoms/Select';
import { DefaultDecoration } from '../InputField/InputField.decorators';

const generateUniqueNumbersArray = (length: number, maxNumber: number): number[] => {
  const array = [];
  while (array.length < length) {
    const number = Math.floor(Math.random() * maxNumber) + 1;
    if (array.indexOf(number) === -1) array.push(number);
  }

  array.sort();

  return array;
};

const ordinalSuffix = (i: number, { first, second, third, defaultSuffix }: Suffixes): string => {
  if (i === 1) {
    return `${i}${first}`;
  }
  if (i === 2) {
    return `${i}${second}`;
  }
  if (i === 3) {
    return `${i}${third}`;
  }
  return `${i}${defaultSuffix}`;
};

const VerificationInput = ({
  numberOfValues = 3,
  maxNumber = 8,
  onChange = () => {},
  dataPointer,
  selectNullText = 'Pick',
  color = 'primary',

  numbersArray = [
    { title: '0', const: '0' },
    { title: '1', const: '1' },
    { title: '2', const: '2' },
    { title: '3', const: '3' },
    { title: '4', const: '4' },
    { title: '5', const: '5' },
    { title: '6', const: '6' },
    { title: '7', const: '7' },
    { title: '8', const: '8' },
    { title: '9', const: '9' },
  ],

  ordinalSuffixes = {
    first: 'st',
    second: 'nd',
    third: 'rd',
    defaultSuffix: 'th',
  },

  invalid,
}: VerificationInputProps) => {
  const [positionsArray] = useState(generateUniqueNumbersArray(numberOfValues, maxNumber));
  const [inputArray, setInputArray] = useState<VerificationInputPayload[]>([]);

  useEffect(() => {
    const shouldUpdate = inputArray.length === numberOfValues;

    const fakeEvent = {
      currentTarget: {
        value: shouldUpdate ? inputArray : [],
        getAttribute: (value: string) => {
          if (value === 'type') return 'change';
          if (value === 'data-pointer') return dataPointer;
          return null;
        },
      },
    };

    onChange(fakeEvent);
  }, [inputArray]);

  const selectProps = {
    initialiseAsNull: true,
    graphical: true,
    nullText: selectNullText,
    controlled: true,
  };

  const handleSelect = (e: React.FormEvent<HTMLSelectElement>, position: number) => {
    const target = e.target as HTMLSelectElement;
    const newArray = inputArray.filter((x) => x.position !== position);

    if (target.value) {
      const selectedNumber = parseInt(target.value, 10);
      newArray.push({ position, value: selectedNumber });
      setInputArray(newArray);
      return;
    }

    setInputArray(newArray);
  };

  return (
    <div className={styles.verificationSelect} data-pointer={dataPointer}>
      <div className={styles.verificationWrapper}>
        {positionsArray.map((position, index) => {
          const inputId = `id_verification_input_${position}_${index}`;
          return (
            <div key={inputId} className={styles.selectContainer}>
              <DefaultDecoration
                key={inputId}
                className={styles.selectContainer}
                title={ordinalSuffix(position, ordinalSuffixes)}
                color={color}
                hideError>
                <Select
                  key={position}
                  id={inputId}
                  options={numbersArray}
                  {...selectProps}
                  className={styles.select}
                  onChange={(e) => handleSelect(e, position)}
                  color={color}
                  controlledText={
                    inputArray.find((x) => x.position === position)?.value.toString() || selectProps.nullText
                  }
                  invalid={invalid}
                />
              </DefaultDecoration>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VerificationInput;
